import React from 'react';
import { Form, Row, Col, Table, Button, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatCurrency, formatDateOnly, formatQuantity } from '../../utils/commonUtils';

const PurchaseOrderDetails = ({
    purchaseOrder,
    setPurchaseOrder,
    isEditing,
    vendors,
    warehouses,
    handleVendorChange,
    handleManufacturerChange,  // NEW: Handler for manufacturer selection
    newLine,
    setNewLine,
    handleAddLine,
    handleRemoveLine,
    handleStockItemChange,
    availableStockItems,
    useFreeText,
    setUseFreeText,
    calculateTotal,
    mode,
    onSubmit,
    onEdit,
    onDownload,
    onCancel,
    isPOCompleted,
    error,
    setError,
    onConvertTemporaryItem
}) => {
    const { hasPermission } = usePermissions();
    return (
        <Form onSubmit={onSubmit}>
            <Row>
                <Col md={3}>
                    <Form.Group className="mb-4" controlId="poNumber">
                        <Form.Label className="fw-bold">PO Number*</Form.Label>
                        {isEditing ? (
                            <Form.Control
                                type="text"
                                value={purchaseOrder.po_number || ''}
                                onChange={(e) => setPurchaseOrder({
                                    ...purchaseOrder,
                                    po_number: e.target.value
                                })}
                                placeholder="Enter PO number"
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={purchaseOrder.po_number || ''}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col md={3}>
                    {/* MANUFACTURER FIELD: Select manufacturer FIRST to filter stock items
                        All items in a PO must be from the same manufacturer */}
                    <Form.Group className="mb-4" controlId="manufacturer">
                        <Form.Label className="fw-bold">Manufacturer*</Form.Label>
                        {isEditing ? (
                            <Select
                                value={vendors.find(v => v._id === purchaseOrder.manufacturer) || null}
                                onChange={handleManufacturerChange}
                                options={vendors}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option._id}
                                isDisabled={mode === 'update'}
                                placeholder="Select Manufacturer"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable={true}
                                isSearchable={true}
                            />
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={vendors.find(v => v._id === purchaseOrder.manufacturer)?.name || ''}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col md={3}>
                    {/* VENDOR FIELD: The supplier/distributor we're purchasing from
                        Selected AFTER manufacturer to determine where to send the PO */}
                    <Form.Group className="mb-4" controlId="vendor">
                        <Form.Label className="fw-bold">Vendor*</Form.Label>
                        {isEditing ? (
                            <Select
                                value={vendors.find(v => v._id === purchaseOrder.vendor) || null}
                                onChange={handleVendorChange}
                                options={vendors}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option._id}
                                isDisabled={mode === 'update' || !purchaseOrder.manufacturer}
                                placeholder={purchaseOrder.manufacturer ? "Select Vendor" : "Select Manufacturer First"}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable={true}
                                isSearchable={true}
                            />
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={vendors.find(v => v._id === purchaseOrder.vendor)?.name || ''}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-4" controlId="shipTo">
                        <Form.Label className="fw-bold">Ship To*</Form.Label>
                        {isEditing ? (
                            <Select
                                value={warehouses.find(w => w._id === purchaseOrder.ship_to) || null}
                                onChange={(selected) => setPurchaseOrder({
                                    ...purchaseOrder,
                                    ship_to: selected ? selected._id : ''
                                })}
                                options={warehouses}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option._id}
                                placeholder="Select Warehouse"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable={true}
                                isSearchable={true}
                            />
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={warehouses.find(w => w._id === purchaseOrder.ship_to)?.name || ''}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-4" controlId="dateCreated">
                        <Form.Label className="fw-bold">Date Created*</Form.Label>
                        {isEditing ? (
                            <Form.Control
                                type="date"
                                value={purchaseOrder.date_created ?
                                    (purchaseOrder.date_created instanceof Date
                                        ? purchaseOrder.date_created.toISOString().split('T')[0]
                                        : purchaseOrder.date_created)
                                    : ''}
                                onChange={e => setPurchaseOrder({
                                    ...purchaseOrder,
                                    date_created: e.target.value ? new Date(e.target.value + 'T00:00:00') : new Date()
                                })}
                                className="w-100"
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={purchaseOrder.date_created ? formatDateOnly(purchaseOrder.date_created) : ''}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-4" controlId="desiredDeliveryDate">
                        <Form.Label className="fw-bold">Delivery Date*</Form.Label>
                        {isEditing ? (
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setUseFreeText(!useFreeText)
                                        setPurchaseOrder(prev => ({
                                            ...prev,
                                            desired_delivery_date: null,
                                            desired_delivery_text: ''
                                        }))
                                    }}
                                    style={{ 
                                        width: '38px', 
                                        height: '38px',
                                        padding: 0,
                                        fontSize: '1.2rem',
                                        flexShrink: 0
                                    }}
                                >
                                    {useFreeText ? '📅' : '✏️'}
                                </Button>
                                {!useFreeText ? (
                                    <Form.Control
                                        type="date"
                                        value={purchaseOrder.desired_delivery_date || ''}
                                        onChange={e => setPurchaseOrder({
                                            ...purchaseOrder,
                                            desired_delivery_date: e.target.value,
                                            desired_delivery_text: ''
                                        })}
                                        className="w-100"
                                        required={!useFreeText}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                ) : (
                                    <Form.Control
                                        type="text"
                                        value={purchaseOrder.desired_delivery_text || ''}
                                        onChange={(e) => setPurchaseOrder({
                                            ...purchaseOrder,
                                            desired_delivery_text: e.target.value,
                                            desired_delivery_date: null
                                        })}
                                        placeholder="Enter delivery information"
                                        required={useFreeText}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                )}
                            </div>
                        ) : (
                            <Form.Control
                                plaintext
                                readOnly
                                value={purchaseOrder.desired_delivery_text ||
                                    (purchaseOrder.desired_delivery_date ?
                                        formatDateOnly(purchaseOrder.desired_delivery_date) : '')}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                {(isEditing || purchaseOrder.internal_notes) && (
                    <Col md={6}>
                        <Form.Group controlId="internalNotes">
                            <Form.Label className="fw-bold">Internal Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={purchaseOrder.internal_notes || ''}
                                onChange={(e) => setPurchaseOrder({
                                    ...purchaseOrder,
                                    internal_notes: e.target.value
                                })}
                                placeholder="Add internal comments and remarks"
                                readOnly={!isEditing}
                                plaintext={!isEditing}
                                className={!isEditing ? "form-control-plaintext border-0 bg-transparent" : ""}
                                style={{ resize: 'none' }}
                                autoComplete="off"
                                spellCheck="true"
                            />
                        </Form.Group>
                    </Col>
                )}
                {(isEditing || purchaseOrder.notes_to_vendor) && (
                    <Col md={6}>
                        <Form.Group controlId="notesToVendor">
                            <Form.Label className="fw-bold">Notes to Vendor</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={purchaseOrder.notes_to_vendor || ''}
                                onChange={(e) => setPurchaseOrder({
                                    ...purchaseOrder,
                                    notes_to_vendor: e.target.value
                                })}
                                placeholder="Add notes that will appear on the purchase order"
                                readOnly={!isEditing}
                                plaintext={!isEditing}
                                className={!isEditing ? "form-control-plaintext border-0 bg-transparent" : ""}
                                style={{ resize: 'none' }}
                                autoComplete="off"
                                spellCheck="true"
                            />
                        </Form.Group>
                    </Col>
                )}
            </Row>

            <h3 className="mt-4">Purchase Order Lines</h3>

            {purchaseOrder.vendor && isEditing && hasPermission('po.modify') && (
                <div className="card mb-3">
                    <div className="card-header bg-body-secondary">
                        <h5 className="mb-0">Add Item to Purchase Order</h5>
                    </div>
                    <div className="card-body">
                        <Row className="mb-3">
                            <Col>
                                <Form.Check
                                    type="checkbox"
                                    id="isTemporaryItem"
                                    label="Temporary Item (details TBD)"
                                    checked={newLine.is_temporary_item || false}
                                    onChange={(e) => setNewLine({
                                        ...newLine,
                                        is_temporary_item: e.target.checked,
                                        stock_item: '',
                                        temporary_item_name: '',
                                        vendor_item_id: '',
                                        price: ''
                                    })}
                                    className="fw-bold"
                                />
                                <Form.Text className="text-muted">
                                    Check this if the item is not yet in the catalog and you don&apos;t have complete details.
                                </Form.Text>
                                {newLine.is_temporary_item && (
                                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                                        <i className="fas fa-exclamation-triangle"></i> Temporary items must be converted to Stock Items before receiving inventory.
                                    </Alert>
                                )}
                            </Col>
                        </Row>
                        <Row className="align-items-end">
                            {newLine.is_temporary_item ? (
                                <>
                                    <Col md={3}>
                                        <Form.Group controlId="temporaryItemName">
                                            <Form.Label className="fw-bold">Item Name/Description*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={newLine.temporary_item_name || ''}
                                                onChange={(e) => setNewLine({
                                                    ...newLine,
                                                    temporary_item_name: e.target.value
                                                })}
                                                placeholder="Enter temporary item name"
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLineVendorItemId">
                                            <Form.Label className="fw-bold">Item ID (Optional)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={newLine.vendor_item_id || ''}
                                                onChange={(e) => setNewLine(prev => ({
                                                    ...prev,
                                                    vendor_item_id: e.target.value
                                                }))}
                                                placeholder="Enter item ID"
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLineQuantity">
                                            <Form.Label className="fw-bold">Quantity*</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={newLine.quantity}
                                                onChange={(e) => setNewLine({
                                                    ...newLine,
                                                    quantity: e.target.value ? parseInt(e.target.value) : ''
                                                })}
                                                min="1"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9]/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLinePrice">
                                            <Form.Label className="fw-bold">Price*</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={newLine.price}
                                                onChange={(e) => setNewLine({
                                                    ...newLine,
                                                    price: e.target.value ? parseFloat(e.target.value) : ''
                                                })}
                                                min="0.01"
                                                placeholder="0.00"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9.]/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Button
                                            variant="warning"
                                            onClick={handleAddLine}
                                            disabled={!newLine.temporary_item_name || !newLine.quantity || !newLine.price || newLine.quantity <= 0 || newLine.price <= 0}
                                            className="w-100"
                                        >
                                            <i className="fas fa-plus"></i> Add Temp
                                        </Button>
                                    </Col>
                                </>
                            ) : (
                                <>
                                    <Col md={3}>
                                        <Form.Group controlId="newLineItem">
                                            <Form.Label className="fw-bold">Stock Item*</Form.Label>
                                            <Select
                                                value={newLine.stock_item ? availableStockItems.find(item => item._id === newLine.stock_item) : null}
                                                onChange={handleStockItemChange}
                                                options={availableStockItems}
                                                getOptionLabel={option => option.description}
                                                getOptionValue={option => option._id}
                                                placeholder={availableStockItems.length === 0 ? "No items available" : "Select Item"}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                isDisabled={availableStockItems.length === 0}
                                                isClearable={true}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLineVendorItemId">
                                            <Form.Label className="fw-bold">Item ID*</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={newLine.vendor_item_id || ''}
                                                onChange={(e) => setNewLine(prev => ({
                                                    ...prev,
                                                    vendor_item_id: e.target.value
                                                }))}
                                                placeholder="Enter item ID"
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLineQuantity">
                                            <Form.Label className="fw-bold">Quantity*</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={newLine.quantity}
                                                onChange={(e) => setNewLine({
                                                    ...newLine,
                                                    quantity: e.target.value ? parseInt(e.target.value) : ''
                                                })}
                                                min="1"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9]/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group controlId="newLinePrice">
                                            <Form.Label className="fw-bold">Price*</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={newLine.price}
                                                onChange={(e) => setNewLine({
                                                    ...newLine,
                                                    price: e.target.value ? parseFloat(e.target.value) : ''
                                                })}
                                                min="0.01"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9.]/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Button
                                            variant="success"
                                            onClick={handleAddLine}
                                            disabled={!newLine.stock_item || !newLine.quantity ||
                                                     !newLine.price || !newLine.vendor_item_id ||
                                                     newLine.quantity <= 0 || newLine.price <= 0}
                                            className="w-100"
                                        >
                                            <i className="fas fa-plus"></i> Add Line
                                        </Button>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </div>
                </div>
            )}

            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>GTIN</th>
                        <th>Description</th>
                        <th>Item ID</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Total</th>
                        {isEditing && hasPermission('po.modify') && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {purchaseOrder.lines.map((line, index) => (
                        <tr key={index}>
                            <td>
                                {line.is_temporary_item ? (
                                    <span className="text-muted">
                                        <i className="fas fa-question-circle"></i>
                                    </span>
                                ) : line.stock_item_image ? (
                                    <img 
                                        src={line.stock_item_image} 
                                        alt="Stock Item Thumbnail"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : null}
                            </td>
                            <td>
                                {line.is_temporary_item ? (
                                    <span className="text-muted">-</span>
                                ) : (
                                    line.stock_item_gtin
                                )}
                            </td>
                            <td>
                                {line.stock_item_description}
                                {line.is_temporary_item && (
                                    <span className="badge bg-warning text-dark ms-2">
                                        <i className="fas fa-exclamation-triangle"></i> TEMP
                                    </span>
                                )}
                            </td>
                            <td>
                                {isEditing && hasPermission('po.modify') ? (
                                    <Form.Control
                                        type="text"
                                        value={line.vendor_item_id || ''}
                                        onChange={(e) => {
                                            const updatedLines = [...purchaseOrder.lines];
                                            updatedLines[index] = {
                                                ...line,
                                                vendor_item_id: e.target.value.toUpperCase()
                                            };
                                            setPurchaseOrder(prev => ({
                                                ...prev,
                                                lines: updatedLines
                                            }));
                                        }}
                                        size="sm"
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                ) : (
                                    line.vendor_item_id
                                )}
                            </td>
                            <td>
                                {isEditing && hasPermission('po.modify') ? (
                                    <Form.Control
                                        type="number"
                                        value={line.quantity}
                                        onChange={(e) => {
                                            const updatedLines = [...purchaseOrder.lines];
                                            updatedLines[index] = {
                                                ...line,
                                                quantity: parseInt(e.target.value) || 0
                                            };
                                            setPurchaseOrder(prev => ({
                                                ...prev,
                                                lines: updatedLines
                                            }));
                                        }}
                                        min="1"
                                        size="sm"
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                ) : (
                                    formatQuantity(line.quantity)
                                )}
                            </td>
                            <td>
                                {isEditing && hasPermission('po.modify') ? (
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={line.price}
                                        onChange={(e) => {
                                            const updatedLines = [...purchaseOrder.lines];
                                            updatedLines[index] = {
                                                ...line,
                                                price: parseFloat(e.target.value) || 0
                                            };
                                            setPurchaseOrder(prev => ({
                                                ...prev,
                                                lines: updatedLines
                                            }));
                                        }}
                                        min="0.01"
                                        size="sm"
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                ) : (
                                    line.price
                                )}
                            </td>
                            <td>{formatCurrency(line.quantity * line.price)}</td>
                            {isEditing && hasPermission('po.modify') && (
                                <td>
                                    <div className="d-flex gap-2">
                                        {line.is_temporary_item && onConvertTemporaryItem && mode !== 'create' && line._id && (
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => onConvertTemporaryItem(line)}
                                                title="Convert this temporary item to a Stock Item"
                                            >
                                                <i className="fas fa-exchange-alt me-1"></i>
                                                Convert
                                            </Button>
                                        )}
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleRemoveLine(index)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                </td>
                            )}
                        </tr>
                    ))}
                    <tr>
                        <td colSpan={isEditing && hasPermission('po.modify') ? "7" : "6"} className="text-end">
                            <strong>Total:</strong>
                        </td>
                        <td><strong>{formatCurrency(calculateTotal())}</strong></td>
                    </tr>
                </tbody>
            </Table>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
                    {error}
                </Alert>
            )}
            
            <div className="d-flex justify-content-end gap-2 mt-4">
                {mode === 'create' || isEditing ? (
                    <>
                        <Button
                            variant="outline-secondary"
                            onClick={(e) => {
                                e.preventDefault();
                                onCancel();
                            }}
                            type="button"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!purchaseOrder.vendor || !purchaseOrder.ship_to || !purchaseOrder.po_number || purchaseOrder.lines.length === 0}
                        >
                            {mode === 'create' ? 'Create' : 'Save'} Purchase Order
                        </Button>
                    </>
                ) : (
                    !isPOCompleted && hasPermission('po.modify') && (
                        <Button
                            variant="primary"
                            onClick={(e) => {
                                e.preventDefault();
                                onEdit();
                            }}
                            type="button"
                        >
                            Edit Purchase Order
                        </Button>
                    )
                )}
                {(mode === 'update' || (mode === 'create' && purchaseOrder.vendor && purchaseOrder.ship_to && purchaseOrder.lines.length > 0)) && (
                    <Button
                        variant="outline-primary"
                        onClick={(e) => {
                            e.preventDefault();
                            onDownload();
                        }}
                        type="button"
                    >
                        <i className="fas fa-download me-2"></i>
                        Download PO
                    </Button>
                )}
            </div>
        </Form>
    );
};

export default PurchaseOrderDetails; 